/**
 * Liftoff (crash) contract: zod inputs, public state, socket events.
 *
 * Time: every timestamp is SERVER epoch ms. Clients estimate `offset = serverNow − Date.now()` from each event
 * and draw m(t) = e^(k·t) themselves (`@tg/games/crash`), t = serverNow' − startedAt. The server only sends
 * phase changes (betting → flying → crashed), a 1 s `crash:tick` for resync, bets and cash-outs.
 * Multipliers on the wire are numbers with 2 decimals (2.47 = 2.47×).
 */
import { z } from 'zod';
import { zAmount, zIdempotencyKey } from '../common';
import type { Ack } from '../socket';

export const CRASH_SLOTS = [0, 1] as const;
export type CrashSlot = (typeof CRASH_SLOTS)[number];

/** Multiplier with at most 2 decimals, 1.01 … 10,000. Server re-validates against the game config. */
export const zCrashMultiplier = z
  .number()
  .finite()
  .min(1.01)
  .max(10_000)
  .refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6, { message: 'max 2 decimals' });

export const zCrashBet = z
  .object({
    slot: z.union([z.literal(0), z.literal(1)]),
    amount: zAmount,
    /** null = manual only */
    autoCashout: zCrashMultiplier.nullable(),
    idempotencyKey: zIdempotencyKey,
  })
  .strict();
export type CrashBetInput = z.input<typeof zCrashBet>;

export const zCrashBetRef = z.object({ betId: z.string().uuid() }).strict();
export type CrashBetRef = z.input<typeof zCrashBetRef>;

export type CrashPhase = 'betting' | 'flying' | 'crashed';

export interface CrashRoundState {
  roundId: string;
  /** global running number shown as "Round #184203" */
  roundNo: number;
  phase: CrashPhase;
  /** sha256(serverSeed), published when betting opens */
  serverSeedHash: string;
  /** betting closes at (server ms) */
  bettingEndsAt: number;
  /** take-off (server ms); null while betting */
  startedAt: number | null;
  /** only once crashed */
  crashPoint: number | null;
  /** server ms of the crash; null before */
  crashedAt: number | null;
  /** revealed once crashed; clientSeed = `round:<roundId>`, nonce 0 */
  serverSeed: string | null;
  /** when the next betting phase opens (crashed phase only) */
  nextRoundAt: number | null;
}

export type CrashBetStatus = 'active' | 'cashed' | 'lost' | 'refunded';

/** A bet in the round players table (public: no user id). */
export interface CrashPlayerBet {
  betId: string;
  roundId: string;
  nickname: string;
  avatar: string;
  slot: CrashSlot;
  amount: number;
  status: CrashBetStatus;
  /** cash-out multiplier once cashed */
  cashout: number | null;
  payout: number | null;
}

/** The player's own bet (panel state). */
export interface CrashMyBet extends CrashPlayerBet {
  autoCashout: number | null;
  idempotencyKey: string;
}

export interface CrashHistoryItem {
  roundId: string;
  roundNo: number;
  crashPoint: number;
}

/** Public game parameters (from @tg/games CRASH_CONFIG + admin overrides). */
export interface CrashPublicConfig {
  minBet: number;
  maxBet: number;
  maxWin: number;
  growthPerMs: number;
  bettingMs: number;
  pauseMs: number;
  minAutoCashout: number;
  maxMultiplier: number;
  houseEdge: number;
}

export interface CrashSnapshot {
  serverNow: number;
  round: CrashRoundState | null;
  players: CrashPlayerBet[];
  history: CrashHistoryItem[];
  /** own bets in the current round (restored after reconnect); empty for guests */
  myBets: CrashMyBet[];
  config: CrashPublicConfig;
}

export interface CrashBetResult {
  bet: CrashMyBet;
  balance: number;
}

export interface CrashCashoutItem {
  betId: string;
  cashout: number;
  payout: number;
}

/** `crash:feed` — batched public changes of one round's bet list. */
export interface CrashFeedEvent {
  roundId: string;
  /** new bets in acceptance order */
  bets: CrashPlayerBet[];
  /** bet ids cancelled (refunded) before take-off */
  cancelled: string[];
  cashouts: CrashCashoutItem[];
}

export interface CrashServerToClient {
  /** phase change: betting opened / took off / crashed */
  'crash:round': (e: { round: CrashRoundState; serverNow: number }) => void;
  /** every second in flight — resync of the client clock */
  'crash:tick': (e: { roundId: string; serverNow: number; elapsed: number }) => void;
  /**
   * Changes of the public bet list, batched (one event per ~200 ms instead of one per bet — with 1000 players the
   * per-bet broadcast is O(bets × players)). Apply in order: `bets` (add/replace), then `cancelled`, then `cashouts`.
   * Always flushed before the next `crash:round`, so a phase change never overtakes the bets of its round.
   */
  'crash:feed': (e: CrashFeedEvent) => void;
  /** own bet changed on the server (auto cash-out executed, lost at crash) — sent to room user:<id> only */
  'crash:my': (e: CrashMyBet) => void;
}

export interface CrashClientToServer {
  /** joins room game:crash and returns the full state (incl. own active bets after a reconnect) */
  'crash:join': (ack: Ack<CrashSnapshot>) => void;
  /** place a bet for the current betting phase (idempotent by idempotencyKey) */
  'crash:bet': (input: CrashBetInput, ack: Ack<CrashBetResult>) => void;
  /** cancel (refund) a bet before take-off */
  'crash:cancel': (input: CrashBetRef, ack: Ack<CrashBetResult>) => void;
  /** manual cash-out, checked against the server clock */
  'crash:cashout': (input: CrashBetRef, ack: Ack<CrashBetResult>) => void;
}
